import { api } from '../serverClient';

import { getServerUrl } from '../updateChannel';
const MATCHMAKING_URL = () => getServerUrl();

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

export async function loadArenaProfile(token) {
  if (!token) return null;

  const res = await fetch(`${MATCHMAKING_URL()}/profile/me`, {
    headers: authHeaders(token),
  });
  if (!res.ok) return null;
  return res.json();
}

// Save mutable profile fields. The collection is intentionally NOT sent
// here. All collection mutations go through grantCards (or the auction
// and mail routes), which use the atomic INSERT … ON CONFLICT path on
// the server. Sending the collection on PUT /me used to corrupt the
// database when the client had multiple foiling variants of the same
// card; that path is now closed.
export async function saveArenaProfile(profile) {
  const token = profile?.serverToken;
  if (!token) throw new Error('No auth token in profile');

  // coins / xp / arcanaShards are intentionally NOT sent. The server's
  // updatePlayer fieldMap no longer accepts them (commit d78f3e2 on the
  // server), so any value we send here is silently discarded. Currency
  // mutations go through dedicated atomic endpoints: match rewards via
  // /me/match/claim, achievement rewards via /me/achievements/claim,
  // pack purchases via /me/packs/purchase, etc.
  const payload = {};
  if (profile.name && profile.name.trim().length >= 2) payload.name = profile.name;
  if (profile.starterDeck != null) payload.starterDeck = profile.starterDeck;
  if (profile.profileAvatar !== undefined) payload.profileAvatar = profile.profileAvatar;
  if (profile.discordHandle !== undefined) payload.discordHandle = profile.discordHandle;
  if (profile.rank) payload.rank = profile.rank;
  if (profile.matchHistory) payload.matchHistory = profile.matchHistory;
  if (profile.seasonProgress !== undefined) payload.seasonProgress = profile.seasonProgress;
  // Discord-style personality + availability. customStatus is omitted
  // here on purpose. It's auto-derived from the user's current activity
  // and pushed via setActivityLabel() (POST /profile/me/activity), so
  // piggybacking it on every full-profile save would overwrite the live
  // value with a stale one.
  if (profile.bio !== undefined) payload.bio = profile.bio;
  if (profile.pronouns !== undefined) payload.pronouns = profile.pronouns;
  if (profile.presenceStatus !== undefined) payload.presenceStatus = profile.presenceStatus;
  if (profile.presenceExpiresAt !== undefined) payload.presenceExpiresAt = profile.presenceExpiresAt;
  if (profile.displayedCrownAvatarId !== undefined) payload.displayedCrownAvatarId = profile.displayedCrownAvatarId;
  if (profile.showcaseCards !== undefined) payload.showcaseCards = profile.showcaseCards;
  if (profile.backgroundCardId !== undefined) payload.backgroundCardId = profile.backgroundCardId;
  if (profile.pinnedBadgeIds !== undefined) payload.pinnedBadgeIds = profile.pinnedBadgeIds;

  const res = await fetch(`${MATCHMAKING_URL()}/profile/me`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to save profile');
  return res.json();
}

// Push the user's auto-derived activity label as their visible
// "current status" to friends. Hits a slim dedicated endpoint that just
// updates custom_status and re-broadcasts presence. Keeps activity
// transitions cheap (no full profile/collection round-trip) and means a
// stale full-profile save can't overwrite the live activity. Pass null
// or '' to clear.
export async function setActivityLabel(label) {
  return api.post('/profile/me/activity', { label: label ?? null });
}

// Bulk-grant cards to the current player. items is an array of
// { cardId, foiling, quantity }. Returns the updated collection so the
// caller can sync local state without an additional GET.
export async function grantCards(items) {
  if (!Array.isArray(items) || items.length === 0) return { collection: [] };
  return api.post('/profile/me/cards/grant', { items });
}

// Claim the next progression deck reward. The server validates that
// the player is at the right level, hasn't already owned this deck,
// and that the deck belongs to the current milestone pool. On success
// it grants the cards and appends the deck id to starter_decks_owned
// in one transaction. Now also handles the L1 starter pick. Owned===0
// triggers the L1 branch which additionally stamps the legacy
// starter_deck column.
export async function claimProgressionDeck(deckId, items) {
  return api.post('/profile/me/progression/claim-deck', { deckId, items });
}

// Hydrate the Progression screen with the player's current claim
// state. Server returns the snapshot shape documented in
// routes/profile.js#buildProgressionSnapshot. Level, XP-into-level,
// per-type "next claimable" levels, grant-level arrays, etc.
export async function getProgression() {
  return api.get('/profile/me/progression');
}

// Claim the gold cell at `level`. Server enforces strict in-order
// (level must equal nextUnclaimedGoldLevel for the player) and that
// the player has actually reached `level`. Returns the new full
// progression snapshot + the credited coin amount.
export async function claimProgressionGold(level) {
  return api.post('/profile/me/progression/claim-gold', { level });
}

// Claim the skill-point cell at `level`. Same in-order + level-
// reached enforcement as the gold endpoint. Bumps granted_skill_points
// by one on the server, which immediately unlocks the new SP in the
// skill-tree spend endpoint.
export async function claimProgressionSkill(level) {
  return api.post('/profile/me/progression/claim-skill', { level });
}

// Record newly-detected achievements on the server. Earn-only. No
// keystone is granted; rows land with claimed_at NULL and the player
// has to click the medallion in the panel (claimAchievement below) to
// collect the reward. Idempotent. Returns { achievements,
// unclaimedAchievements, newlyDetected }.
export async function detectAchievements(achievementIds) {
  if (!Array.isArray(achievementIds) || achievementIds.length === 0) {
    return { achievements: null, unclaimedAchievements: null, newlyDetected: [] };
  }
  return api.post('/profile/me/achievements/detect', { achievementIds });
}

// Collect the keystone reward for ONE previously-detected achievement
// (the click-to-claim ceremony). Server returns { claimed: true,
// achievementId, keystones, keystonesGranted, unclaimedAchievements }
// on success, or { claimed: false, reason: 'already-claimed' |
// 'invalid', ... } on no-op. Achievement keystone grants land only
// through this endpoint.
export async function claimAchievement(achievementId) {
  if (typeof achievementId !== 'string' || achievementId.length === 0) {
    return { claimed: false, reason: 'invalid' };
  }
  return api.post('/profile/me/achievements/claim', { achievementId });
}

// Spend a single skill point on the server. The server is the source
// of truth for skill state since skill-gated mechanics (Auction House
// listing capacity, fee tier, timed bidding unlock) all key off the
// stored blob. Returns the updated { skills } object on success.
export async function spendSkillPoint(nodeKey) {
  return api.post('/profile/me/skills/spend', { nodeKey });
}

// Pick the elemental affinity for the Augmentation/Hunter path.
// Permanent on the server side. Subsequent calls return 400.
export async function setSkillElement(element) {
  return api.post('/profile/me/skills/element', { element });
}

// Player-facing respec. Charges SKILL_RESET_COST gold server-side and
// wipes the spent points + hunter element. Rejects with 402 when the
// player can't afford it and 400 when there is nothing to reset.
// Returns { skills, coins, spent }.
export async function purchaseSkillResetServer() {
  return api.post('/profile/me/skills/purchase-reset', {});
}
